/**
 * Shared layout tokens for Studio surfaces (home, spreadsheets, etc.).
 *
 * Intentionally excludes shell chrome (icon rail, route wrappers, etc.).
 * Orchestration lives in `applications/App/index.jsx`.
 */

import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';

/** Mid-tone of the Studio canvas — use for ClampedText see-more fades */
export const STUDIO_CANVAS_FADE = Constants.StudioCanvasFade || '#fff';

/** Creator canvas background — flat white or full black */
export const STUDIO_CANVAS_GRADIENT = Constants.StudioCanvasGradient || '#fff';

/** Subtle elevation for float cards on the Studio canvas */
export const STUDIO_FLOAT_SHADOW = Constants.StudioFloatShadow || '0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 32px rgba(0, 0, 0, 0.07)';

/** Desktop icon rail width (used when rail mounts in App) */
export const STUDIO_RAIL_WIDTH = 56;

/** Standard icon dimensions in the Studio icon rail */
export const STUDIO_RAIL_ICON = { width: 22, height: 22, flexShrink: 0 };

/** Float card corner radius */
export const STUDIO_FLOAT_RADIUS = '22px';
export const STUDIO_FLOAT_RADIUS_MOBILE = '18px';

/** White elevated card on the Studio canvas (scroll lives inside). */
export const StudioFloatCanvas = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${Constants.BackgroundColor};
  border-radius: ${STUDIO_FLOAT_RADIUS};
  box-shadow: ${STUDIO_FLOAT_SHADOW};
  border: 1px solid rgba(255, 255, 255, 0.95);
  overflow: hidden;

  ${Constants.MediaMobile} {
    border-radius: ${STUDIO_FLOAT_RADIUS_MOBILE};
  }
`;

/** Inset frame between route viewport and float card — uniform gutter on all sides. */
export const StudioPageFrame = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 0.65rem;
  box-sizing: border-box;
  min-height: 0;
`;

/** Full-bleed route viewport (immersive editors — no float-card inset). */
export const StudioImmersiveFrame = styled.div`
  position: absolute;
  inset: 0;
  min-height: 0;
  overflow: hidden;
`;

export const StudioIconRailAside = styled.aside`
  flex-shrink: 0;
  width: ${STUDIO_RAIL_WIDTH}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0 1rem;
  z-index: 2;
  gap: 0.25rem;
`;

export const StudioRailBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 14px;
  background: ${(p) => (p.$active ? '#fff' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 2px 10px rgba(0,0,0,0.08)' : 'none')};
  cursor: pointer;
  transition: background 0.12s, box-shadow 0.12s;
  color: ${(p) => (p.$active ? Constants.PrimaryColor : Constants.DarkTextColor)};

  &:hover {
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

export const StudioRailBrandBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 0.15rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

export const StudioRailLogo = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  display: block;
`;

export const StudioRailSpacer = styled.div`
  flex: 1;
  min-height: 0.5rem;
`;

export const StudioRailProfileBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.12s;

  &:hover {
    transform: scale(1.06);
  }
`;

export const StudioChromeBody = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  min-height: 0;
  overflow: hidden;
`;

export const StudioRouteViewport = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;
